import { useEffect, useState } from "react";

import { type FetchItems, TimelineDataStreamer } from "./TimelineDataStreamer";
import type { TimeWindow } from "./window";

/**
 * One streamer per `fetchItems` identity.
 *
 * A new function (a new filter condition) gets a fresh streamer.
 * The previous one is disposed, which aborts its request so a stale
 * result can never be shown.
 *
 * `prefetch` is a window to ask for as soon as the streamer exists, before
 * the view knows its own — so the first chunk does not wait for the
 * activity to resolve. Keep it referentially stable; it is a dependency.
 */
export function useStreamer<T>(
  fetchItems: FetchItems<T>,
  prefetch?: TimeWindow,
): TimelineDataStreamer<T> | null {
  const [streamer, setStreamer] = useState<TimelineDataStreamer<T> | null>(
    null,
  );

  useEffect(() => {
    const next = new TimelineDataStreamer(fetchItems);
    if (prefetch) {
      next.requestData(prefetch);
    }
    setStreamer(next);
    return () => {
      next.dispose();
      setStreamer((current) => (current === next ? null : current));
    };
  }, [fetchItems, prefetch]);

  return streamer;
}
